import { Injectable } from '@angular/core';
import { createClient } from '@sanity/client';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { TranslationService } from './translation.service';

@Injectable({
  providedIn: 'root',
})
export class SanityService {
  private client;

  constructor(private translationService: TranslationService) {
    this.client = createClient({
      projectId: environment.sanity.projectId,
      dataset: environment.sanity.dataset,
      useCdn: false,
      apiVersion: '2021-03-25',
      token:
        'ske1G8kHAqpZqTTKmq7Q7h7yeHYtQfRLPbyVoBqkCwTw0hMi6X61EfyQ0FSj1FVxp5DvImcakBbNvesUd1z0qz1axKMdn3Z1fsM7OethQPGbRHkic7kbmMBLv2bIApM9bNGVIYmDVmvVuNcQOHlT2VgXwcl8rJd2jN95kw1Z0kBUNOfnWcD3',
    });
  }

  /**
   * Helper method to transform a Sanity image reference into a full URL
   */
  public getImageUrl(imageRef: string | null): string {
    if (!imageRef) {
      return '/assets/images/blog-cover-placeholder.jpg';
    }
    return `https://cdn.sanity.io/images/${environment.sanity.projectId}/${
      environment.sanity.dataset
    }/${imageRef
      .replace('image-', '')
      .replace(/-jpg$/, '.jpg')
      .replace(/-png$/, '.png')
      .replace(/-webp$/, '.webp')
      .replace(/-jpeg$/, '.jpeg')
      .replace(/-gif$/, '.gif')}?w=800&h=400&fit=crop&auto=format&q=80`;
  }

  /**
   * Fetch data using a GROQ query
   */
  public fetch(query: string, params?: Record<string, any>): Observable<any> {
    return from(this.client.fetch(query, params));
  }

  /**
   * Create a new document
   */
  public create(document: any): Observable<any> {
    return from(this.client.create(document));
  }

  /**
   * Get a document patch helper for the given document ID
   */
  public patch(id: string) {
    return this.client.patch(id);
  }

  /**
   * Delete a document by ID
   */
  public delete(id: string): Observable<any> {
    return from(this.client.delete(id));
  }

  /**
   * Fetches blog posts with pagination
   */
  public getBlogPosts(
    page: number = 1,
    pageSize: number = 10
  ): Observable<any> {
    const lang = this.translationService.getCurrentLanguage();
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    const query = `{
      'total': count(*[_type == 'blogPost' && language == '${lang}']),
      'result': *[_type == 'blogPost' && language == '${lang}'] | order(publishedAt desc) [${start}...${end}] {
        _id,
        title,
        'slug': slug.current,
        excerpt,
        'coverImage': select(
          defined(coverImage) => coverImage.asset._ref,
          null
        ),
        publishedAt,
        author,
        categories
      }
    }`;

    return this.fetch(query).pipe(
      map((result) => {
        const total = result?.total || 0;
        const posts = result?.result || [];

        const transformedPosts = posts.map((post: any) => ({
          ...post,
          coverImage: this.getImageUrl(post.coverImage),
        }));

        return {
          data: transformedPosts,
          meta: {
            pagination: {
              page,
              pageSize,
              pageCount: Math.ceil(total / pageSize),
              total: total,
            },
          },
        };
      })
    );
  }

  /**
   * Fetches blog posts by status
   */
  public getBlogPostsByStatus(
    status: 'draft' | 'published' | 'scheduled',
    page: number = 1,
    pageSize: number = 10
  ): Observable<any> {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    const query = `{
      'total': count(*[_type == 'blogPost' && status == '${status}']),
      'result': *[_type == 'blogPost' && status == '${status}'] | order(${
      status === 'scheduled' ? 'scheduledPublishTime' : 'publishedAt'
    } desc) [${start}...${end}] {
        _id,
        title,
        'slug': slug.current,
        language,
        status,
        publishedAt,
        scheduledPublishTime,
        generatedBy,
        'coverImage': select(
          defined(coverImage) => coverImage.asset._ref,
          null
        ),
        'targetKeywords': targetKeywords[]->keyword
      }
    }`;

    return this.fetch(query).pipe(
      map((result) => {
        const total = result?.total || 0;
        const posts = result?.result || [];

        const transformedPosts = posts.map((post: any) => ({
          ...post,
          coverImage: this.getImageUrl(post.coverImage),
        }));

        return {
          data: transformedPosts,
          meta: {
            pagination: {
              page,
              pageSize,
              pageCount: Math.ceil(total / pageSize),
              total: total,
            },
          },
        };
      })
    );
  }

  /**
   * Fetches a specific blog post by slug
   */
  public getBlogPostBySlug(slug: string): Observable<any> {
    const lang = this.translationService.getCurrentLanguage();
    const query = `*[_type == 'blogPost' && slug.current == '${slug}' && language == '${lang}'][0] {
      _id,
      title,
      'slug': slug.current,
      excerpt,
      content,
      'coverImage': select(
        defined(coverImage) => coverImage.asset._ref,
        null
      ),
      publishedAt,
      author,
      categories,
      seo
    }`;

    return this.fetch(query).pipe(
      map((post) => {
        if (!post) return { data: [] };

        const transformedPost = {
          ...post,
          coverImage: this.getImageUrl(post.coverImage),
          seo: post.seo
            ? {
                ...post.seo,
                socialImage: post.seo.socialImage
                  ? this.getImageUrl(post.seo.socialImage)
                  : null,
              }
            : null,
        };

        return { data: [transformedPost] };
      })
    );
  }

  /**
   * Fetches keywords with pagination
   */
  public getKeywords(page: number = 1, pageSize: number = 50): Observable<any> {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    const query = `{
      'total': count(*[_type == 'keyword' && defined(language) && defined(category) && defined(baseKeyword)]),
      'result': *[_type == 'keyword' && defined(language) && defined(category) && defined(baseKeyword)] | order(_createdAt desc) [${start}...${end}] {
        _id,
        language,
        category,
        baseKeyword,
        status,
        longtailKeywords[] {
          keyword,
          searchVolume,
          competition,
          cpc
        }
      }
    }`;

    return this.fetch(query).pipe(
      map((result) => {
        const total = result?.total || 0;
        const keywords = result?.result || [];

        return {
          data: keywords,
          meta: {
            pagination: {
              page,
              pageSize,
              pageCount: Math.ceil(total / pageSize),
              total: total,
            },
          },
        };
      })
    );
  }

  /**
   * Creates a new keyword
   */
  public createKeyword(keywordData: any): Observable<any> {
    const document = {
      _type: 'keyword',
      ...keywordData,
    };
    return this.create(document);
  }

  /**
   * Fetches keyword analysis data for a specific keyword
   */
  /**
   * Fetches all angles from all keyword analyses
   */
  public getAllAngles(): Observable<any> {
    const query = `{
      'total': count(*[_type == 'keywordAnalysis' && defined(blogPostAngles)]),
      'result': *[_type == 'keywordAnalysis' && defined(blogPostAngles)] {
        _id,
        keywordText,
        language,
        'keyword': *[_type == 'keyword' && _id == ^.keyword._ref][0] {
          baseKeyword,
          category
        },
        blogPostAngles[] {
          title,
          description
        },
        createdAt
      }
    }`;

    return this.fetch(query).pipe(
      map((result) => {
        if (!result) return { data: [] };
        return {
          data: result.result.map((item: any) => ({
            ...item,
            blogPostAngles: item.blogPostAngles.map(
              (angle: any, index: number) => ({
                ...angle,
                source:
                  item.keywordText === item.keyword?.baseKeyword
                    ? 'base'
                    : 'longtail',
                index,
              })
            ),
          })),
        };
      })
    );
  }

  public getKeywordAnalysis(
    keywordId: string,
    language: string,
    keyword?: string
  ): Observable<any> {
    const query = `*[_type == 'keywordAnalysis' && keyword._ref == '${keywordId}' && language == '${language}'${
      keyword ? ` && keywordText == "${keyword}"` : ''
    }][0] {
      _id,
      analysis,
      keywordText,
      blogPostAngles,
      regulatoryData,
      createdAt
    }`;

    return this.fetch(query).pipe(
      map((result) => {
        if (!result) return { data: null };
        return { data: result };
      })
    );
  }

  /**
   * Creates a new keyword analysis
   */
  public createKeywordAnalysis(analysisData: any): Observable<any> {
    const document = {
      _type: 'keywordAnalysis',
      ...analysisData,
    };
    return this.create(document);
  }
}
